import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardActions,
  Typography,
  Button,
  Divider,
  TextField,
} from "@mui/material";
import { TaskerRejectOption, isCustomRejectOption } from "@t3/shared";
import { useState, useEffect } from "react";

interface RejectOptionCardProps {
  option: TaskerRejectOption;
  isSelected: boolean;
  onSelect: (option: TaskerRejectOption | null) => void;
  viewOnly?: boolean;
}

export const RejectOptionCard = ({
  option,
  isSelected,
  onSelect,
  viewOnly = false,
}: RejectOptionCardProps) => {
  const [customMessage, setCustomMessage] = useState("");
  const isCustomType = isCustomRejectOption(option);

  useEffect(() => {
    if (isSelected && isCustomType && customMessage) {
      onSelect({
        ...option,
        message: customMessage,
      });
    }
  }, [customMessage, isSelected, isCustomType, option, onSelect]);

  const handleCustomMessageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newMessage = event.target.value;

    if (newMessage === "") {
      onSelect(null);
    }

    setCustomMessage(newMessage);

    onSelect({
      ...option,
      message: newMessage,
    });
  };

  const handleSelect = () => {
    if (isCustomType) {
      if (isSelected) {
        setCustomMessage("");
        onSelect({
          ...option,
          message: "",
        });
      } else if (customMessage) {
        onSelect({
          ...option,
          message: customMessage,
        });
      }
    } else {
      onSelect(option);
    }
  };

  return (
    <Box sx={{ width: "100%" }} data-cypress="reject-option-card">
      <Card
        sx={{
          width: "100%",
          border: (theme) =>
            isSelected
              ? `2px solid ${theme.palette.error.main}`
              : `1px solid ${theme.palette.divider}`,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: (theme) => theme.shadows[4],
          },
          bgcolor: isSelected
            ? (theme) => theme.palette.error.light + "20"
            : "background.paper",
        }}
      >
        <CardActionArea
          disableRipple={isCustomType}
          onClick={() => !viewOnly && !isCustomType && handleSelect()}
        >
          <CardContent>
            {isCustomType ? (
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Enter custom reject reason..."
                value={customMessage}
                onChange={handleCustomMessageChange}
                onClick={(e) => e.stopPropagation()}
                sx={{ mb: 1 }}
              />
            ) : (
              <Typography
                gutterBottom
                variant="h5"
                sx={{ maxWidth: "calc(100% - 100px)" }}
              >
                {option.message}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>

        {!viewOnly && (
          <>
            <Divider />
            <CardActions sx={{ p: 0 }}>
              {isCustomType && !customMessage ? null : (
                <Button
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect();
                  }}
                  sx={{
                    py: 1.5,
                    borderRadius: 0,
                    backgroundColor: isSelected ? "error.main" : "none",
                    color: isSelected ? "white" : "inherit",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "error.dark"
                        : "action.hover",
                    },
                  }}
                >
                  {isSelected ? "Remove Option" : "Select Option"}
                </Button>
              )}
            </CardActions>
          </>
        )}
      </Card>
    </Box>
  );
};
